import type { Server } from 'miragejs';
import { response } from 'mocks/helpers';
import { faker } from '@faker-js/faker/locale/en';

const allowedSeasonValues = [
  { id: 26, label: 'AW25', userDefinedId: null, parentId: null },
  { id: 28, label: 'AW26', userDefinedId: null, parentId: null },
  { id: 27, label: 'SS25', userDefinedId: null, parentId: null },
  { id: 25, label: 'SS26', userDefinedId: null, parentId: null },
  { id: 30, label: 'SS27', userDefinedId: null, parentId: null },
];

/** Matches `CustomFieldDefinition` — aligned with `generateCustomFields` (customFieldId 8). */
const itemCustomFieldDefinitions = [
  {
    customFieldId: 8,
    name: 'Season used',
    userDefinedId: 'SEASON',
    type: 0,
    isMandatory: false,
    isSupplierEditable: true,
    internalOnly: false,
    maxChar: null,
    isMultiLine: true,
    allowedDecimals: 0,
    isMultipleAllowedValue: true,
    allowedValues: allowedSeasonValues,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
  {
    customFieldId: 9,
    name: 'Internal notes',
    userDefinedId: 'NOTES',
    type: 2,
    isMandatory: false,
    isSupplierEditable: false,
    internalOnly: true,
    maxChar: 500,
    isMultiLine: true,
    allowedDecimals: 0,
    isMultipleAllowedValue: false,
    parentCustomFieldId: null,
    parentCustomFieldName: null,
  },
];

const brands = [
  { id: 28, name: 'All' },
  { id: 29, name: 'Brand A' },
  { id: 30, name: 'Brand B' },
  { id: 31, name: 'Brand C' },
];

const organizations = [
  { id: 39, name: 'Delogue Garments' },
  { id: 40, name: 'Delogue Accessories' },
  { id: 41, name: 'Delogue Footwear' },
];

const suppliers = [
  { id: 44, name: 'Ningbo garment supplier' },
  { id: 45, name: 'Shanghai accessories co.' },
  { id: 46, name: 'Guangzhou textile factory' },
  { id: 47, name: 'Shenzhen packaging ltd.' },
  { id: 48, name: 'Hong Kong labels co.' },
];

const adminUnits = [
  { id: 19, name: 'pcs', isFraction: false },
  { id: 20, name: 'kg', isFraction: true },
  { id: 21, name: 'm', isFraction: true },
  { id: 22, name: 'set', isFraction: false },
];

const categories = [
  { id: 87, name: 'Packaging' },
  { id: 88, name: 'Labels' },
  { id: 89, name: 'Tags' },
  { id: 90, name: 'Trims' },
  { id: 91, name: 'Fabric' },
  { id: 92, name: 'Accessories' },
  { id: 93, name: 'Hardware' },
];

const contactPersons = [
  { id: 'd47e406b-d5db-4787-ade6-7225780b5b5c', name: 'Sara Johnson' },
  { id: 'a12b3c4d-e5f6-7890-abcd-ef1234567890', name: 'Mike Chen' },
  { id: 'b23c4d5e-f6a7-8901-bcde-f12345678901', name: 'Anna Larsen' },
  { id: 'c34d5e6f-a7b8-9012-cdef-123456789012', name: 'James Park' },
];

const itemNameParts = [
  'Box',
  'Label',
  'Tag',
  'Button',
  'Zipper',
  'Patch',
  'Ribbon',
  'Thread',
  'Lining',
  'Interlining',
  'Buckle',
  'Hook',
  'Snap',
  'Velcro',
  'Elastic',
  'Cord',
  'Bead',
  'Sequin',
  'Stud',
  'Rivet',
  'Badge',
  'Emblem',
  'Tape',
  'Padding',
  'Shoulder pad',
  'Collar stay',
  'Eyelet',
  'Grommet',
  'Drawstring',
];

const itemDescriptors = [
  'for garments',
  'for jackets',
  'for trousers',
  'for shirts',
  'for dresses',
  'for outerwear',
  'for knitwear',
  'for accessories',
  'woven',
  'printed',
  'metal',
  'plastic',
  'rubber',
  'cotton',
  'polyester',
  'nylon',
  'recycled',
];

const generateCustomFields = (baseId: number) => {
  const count = faker.number.int({ min: 0, max: 3 });
  return Array.from({ length: count }, (_, i) => {
    const allowedValue = faker.helpers.arrayElement(allowedSeasonValues);
    return {
      id: baseId + i,
      customFieldId: 8,
      name: 'Season used',
      userDefinedId: null,
      type: 0,
      value: allowedValue.label,
      itemCustomFieldAllowedValueId: allowedValue.id,
      customFieldAllowedValueUserDefinedID: null,
      isMandatory: false,
      isSupplierEditable: faker.datatype.boolean(),
      internalOnly: false,
      maxChar: null,
      isMultiLine: true,
      allowedDecimals: 0,
      isMultipleAllowedValue: true,
      parentCustomFieldId: null,
      parentCustomFieldName: null,
      allowedValues: allowedSeasonValues,
    };
  });
};

const generateItem = (id: number) => {
  const hasLogo = faker.datatype.boolean();
  const hasDeadline = faker.datatype.boolean();
  const hasErpId = faker.datatype.boolean();
  const hasPhysicalRef = faker.datatype.boolean();
  const categoryCount = faker.number.int({ min: 1, max: 3 });
  const selectedCategories = faker.helpers.arrayElements(categories, categoryCount);
  const itemName = `${faker.helpers.arrayElement(itemNameParts)} ${faker.helpers.arrayElement(
    itemDescriptors
  )}`;

  return {
    id,
    name: itemName,
    userDefinedId: faker.string.numeric(4),
    description: faker.helpers.maybe(() => faker.commerce.productDescription()) ?? null,
    brand: faker.helpers.arrayElement(brands),
    organization: faker.helpers.arrayElement(organizations),
    supplier: faker.helpers.arrayElement(suppliers),
    companyContactPerson: faker.helpers.arrayElement(contactPersons),
    supplierContactPerson: null,
    adminUnit: faker.helpers.arrayElement(adminUnits),
    logo: hasLogo ? { id: faker.string.uuid(), url: faker.image.url() } : null,
    isActive: faker.datatype.boolean(),
    suppliedBy: null,
    readyForExport: faker.datatype.boolean(),
    updatedAfterLastExport: faker.datatype.boolean(),
    lastUpdated: faker.date.recent({ days: 365 }).toISOString().replace('Z', ''),
    deadline: hasDeadline ? faker.date.future().toISOString().split('T')[0] : null,
    etd: hasDeadline ? faker.date.future().toISOString().split('T')[0] : null,
    erpItemId: hasErpId ? `ERP-${faker.string.numeric(4)}` : null,
    defaultQty: faker.number.float({ min: 1, max: 100, fractionDigits: 5 }),
    nameGeneratorExtraText: null,
    physicalReferenceNumber: hasPhysicalRef ? `REF-${id}` : null,
    categories: selectedCategories,
    customFields: generateCustomFields(id * 10),
  };
};

faker.seed(42);

const perPage = 100;
const itemsData = Array.from({ length: 213 }, (_, i) => generateItem(i + 1));

export const itemsEndpoints = (server: Server) => {
  server.get('/api/meta/items/customfields', (_schema, request) =>
    response(request.url, {
      customFieldDefinitions: itemCustomFieldDefinitions,
    })
  );

  server.get('/api/items', (_schema, request) => {
    const url = new URL(request.url, 'http://localhost');
    const cursor = url.searchParams.get('cursor');

    if (cursor) {
      const page = Number(cursor.split('-')[0]) || 1;
      const start = page * perPage;
      const end = page < 2 ? start + perPage : itemsData.length;
      const pageData = itemsData.slice(start, end);

      return response(request.url, pageData, {
        status: 'success',
        code: 'ok',
        pagination: {
          cursor: `${page + 1}-${itemsData[end]?.id}`,
          hasMore: page < 2,
        },
        error: { details: [] },
        statusCode: 200,
      });
    }

    const pageData = itemsData.slice(0, perPage);

    return response(request.url, [], {
      status: 'success',
      code: 'ok',
      pagination: {
        cursor: `1-${itemsData[perPage]?.id}`,
        hasMore: false,
      },
      error: { details: [] },
      statusCode: 200,
    });
  });
};
